import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailDiscountReasonsTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailDiscountReasons = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [discountReason, setDiscountReason] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [retailDiscountReasons, setRetailDiscountReasons] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Discount Reason": discountReason,
    Active: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    if (!discountReason || String(discountReason).trim() === "") {
      setNameError(true);
      return false;
    }
    if (
      !validateString(
        discountReason,
        validationLength.discountReason,
        validationType.NAME,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  const editRetailDiscountReasons = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setDiscountReason(null);
      setActive(true);
      setNameError(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setDiscountReason(list?.["Discount Reason"] ?? null);
    setActive(list?.["Active"] === "Yes");
    setNameError(false);
  };

  const updateRetailDiscountReasons = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailDiscountReasons.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.DISCOUNTREASON,
        discountReason,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setRetailDiscountReasons((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailDiscountReasons(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.discountReasons} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailDiscountReasons = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailDiscountReasons,
        DNDFieldsEnum.DISCOUNTREASON,
        discountReason,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailDiscountReasons((prev: any[]) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailDiscountReasons.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailDiscountReasons(null);
    setEditClicked(false);
  };

  const deleteRetailDiscountReasons = (id: number) => {
    setRetailDiscountReasons(deleteItem(retailDiscountReasons, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailDiscountReasons(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailDiscountReasons);
    if (editClicked) {
      setSelectedId(
        checkData(retailDiscountReasons, selectedId) ? selectedId : null,
      );
    }
  }, [retailDiscountReasons]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Discount Reasons
              <Tooltip
                title={tooltipComponent(retailDiscountReasonsTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailDiscountReasons(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailDiscountReasons(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Discount Reason
              <Tooltip
                title={tooltipComponent(
                  retailDiscountReasonsTooltip.discountReason,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailDiscountReason"
              variant="outlined"
              placeholder="Discount Reason"
              value={discountReason ?? ""}
              helperText={
                nameError
                  ? !discountReason || String(discountReason).trim() === ""
                    ? validationMessages.fieldRequired
                    : validationMessages.retailDiscountReasonName
                  : fieldValidationHelperText.retailDiscountReason
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiscountReason(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(retailDiscountReasonsTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailDiscountReasons
                  : addRetailDiscountReasons
              }
              id="addRetailDiscountReason"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Discount
              Reason
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Discount Reasons Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailDiscountReasons?.length ? (
                <DraggableItemList
                  itemData={retailDiscountReasons}
                  onOrderUpdate={(order) => setRetailDiscountReasons(order)}
                  onEdit={(info) => {
                    editRetailDiscountReasons(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailDiscountReasons(id)}
                  code={DNDFieldsEnum.DISCOUNTREASON}
                  name={""}
                />
              ) : (
                <Typography variant="body2">
                  No Discount Reasons Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailDiscountReasons;
